import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Alert,
  Card,
  CardContent,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  EventAvailable,
  CalendarMonth,
  Groups,
  AccessTime,
} from "@mui/icons-material";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import ChartCard from "@/components/data-visuals/ChartCard";
import HubMedOverviewStatCard from "@/components/admin/hubmed/HubMedOverviewStatCard";
import HubMedAppointmentsChart from "@/components/admin/hubmed/HubMedAppointmentsChart";
import { mapConsultTypeBreakdownToPie } from "@/components/admin/hubmed/hubMedChartHelpers";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import {
  adminDateRangePickerWideNestedSx,
  ADMIN_TOP_CARD_HEIGHT,
} from "@/utils/adminTableStyles";

const HOLA_DARK = "#005760";
const HOLA_LIGHT = "#FFF087";

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    dateStart: start.toISOString().split("T")[0],
    dateEnd: end.toISOString().split("T")[0],
  };
};

const holaDateRangeSx = {
  borderRadius: "8px",
  p: 1.5,
  minHeight: ADMIN_TOP_CARD_HEIGHT,
  height: ADMIN_TOP_CARD_HEIGHT,
  maxHeight: ADMIN_TOP_CARD_HEIGHT,
  boxSizing: "border-box",
  backgroundColor: HOLA_DARK,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "center",
  gap: 0.5,
  overflow: "hidden",
  "& .text-black": { color: "white !important" },
  ...adminDateRangePickerWideNestedSx,
};

const holaStatCardSx = {
  borderColor: HOLA_LIGHT,
  backgroundColor: HOLA_DARK,
};

const HolaStatCard = ({ title, value, valueFormatter, Icon }) => (
  <Box className="min-w-0 h-full flex">
    <HubMedOverviewStatCard
      title={title}
      value={value}
      valueFormatter={valueFormatter}
      Icon={Icon}
      sx={holaStatCardSx}
    />
  </Box>
);

const HolaAdminOverviewPage = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const initial = useMemo(() => defaultDateRange(), []);
  const [dateStart, setDateStart] = useState(initial.dateStart);
  const [dateEnd, setDateEnd] = useState(initial.dateEnd);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [timeChartOpen, setTimeChartOpen] = useState(false);

  const onDateRangeChange = useCallback((start, end) => {
    setDateStart(start || "");
    setDateEnd(end || "");
  }, []);

  useEffect(() => {
    if (!dateStart || !dateEnd) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await phappsRequest(
          API_PATHS.HOLA_ADMIN_APPOINTMENT_OVERVIEW,
          "GET",
          null,
          { dateStart, dateEnd }
        );
        if (!cancelled) setOverview(raw ?? null);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setOverview(null);
          setError(getAdminApiErrorMessage(err, "Failed to load Hola overview"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dateStart, dateEnd]);

  const summary = overview?.summary ?? {};

  const totalAppointmentsDisplay = useMemo(() => {
    const n = Number(summary.totalAppointmentsInRange ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, [summary.totalAppointmentsInRange]);

  const avgWeek = summary.averageAppointmentsPerWeek;

  const activeChemistsCount = useMemo(() => {
    const n = Number(summary.totalActiveChemists ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, [summary.totalActiveChemists]);

  const avgWaitDisplay = useMemo(() => {
    const v = summary.averageWaitTimeMinutes;
    if (v == null) return "—";
    const n = Number(v);
    return Number.isFinite(n) ? `${Math.round(n)} min` : "—";
  }, [summary.averageWaitTimeMinutes]);

  const pieSlices = useMemo(
    () => mapConsultTypeBreakdownToPie(overview?.consultTypeBreakdownRange),
    [overview]
  );

  const chemistRows = Array.isArray(overview?.chemists) ? overview.chemists : [];
  const statsLoading = loading && !overview;

  return (
    <div className="h-full min-h-0 flex flex-col bg-white overflow-x-hidden">
      <div className="flex-1 min-h-0 flex flex-col p-4 gap-3 w-full max-w-full">
        {loading && !overview ? (
          <div className="flex flex-1 items-center justify-center min-h-[280px]">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                style={{ borderColor: HOLA_DARK }}
              />
              <p className="text-gray-600">Loading Hola overview…</p>
            </div>
          </div>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: HOLA_DARK, flexShrink: 0 }}>
              Hola Admin overview
            </Typography>

            {error ? (
              <Alert severity="warning" sx={{ flexShrink: 0 }}>{error}</Alert>
            ) : null}

            {/* Filters + stat cards row */}
            <Box
              component="section"
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 items-stretch"
              sx={{ flexShrink: 0 }}
            >
              {/* Date picker */}
              <Box sx={{ ...holaDateRangeSx, flex: 1, minWidth: 0 }}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={(range) => {
                    const start = range[0]?.toISOString().split("T")[0];
                    const end = range[1]?.toISOString().split("T")[0];
                    onDateRangeChange(start, end);
                  }}
                />
              </Box>

              {/* Stat cards */}
              {statsLoading ? (
                [1, 2, 3, 4].map((k) => (
                  <div key={k} className="min-w-0 h-full flex">
                    <Skeleton
                      variant="rounded"
                      sx={{ borderRadius: 2, width: "100%", minHeight: 112, flex: 1 }}
                    />
                  </div>
                ))
              ) : (
                <>
                  <HolaStatCard
                    title="Total appointments"
                    value={totalAppointmentsDisplay}
                    valueFormatter={(v) => Number(v || 0).toLocaleString()}
                    Icon={EventAvailable}
                  />
                  <HolaStatCard
                    title="Appointments per week"
                    value={Number(avgWeek ?? 0).toFixed(2)}
                    valueFormatter={(v) => v}
                    Icon={CalendarMonth}
                  />
                  <HolaStatCard
                    title="Active pharmacies"
                    value={activeChemistsCount}
                    valueFormatter={(v) => Number(v || 0).toLocaleString()}
                    Icon={Groups}
                  />
                  <HolaStatCard
                    title="Avg patient wait"
                    value={avgWaitDisplay}
                    valueFormatter={(v) => v}
                    Icon={AccessTime}
                  />
                </>
              )}
            </Box>

            {/* Chemists table + pie chart */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
                flex: { xs: "none", lg: 1 },
                minHeight: { xs: 0, lg: 0 },
              }}
            >
              {/* Chemist appointment counts */}
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderColor: HOLA_DARK,
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography fontWeight={700} sx={{ color: HOLA_DARK }}>
                      Pharmacies
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setTimeChartOpen(true)}
                      disabled={!overview}
                      sx={{
                        textTransform: "none",
                        borderColor: HOLA_DARK,
                        color: HOLA_DARK,
                        fontWeight: 600,
                      }}
                    >
                      See appointments over time
                    </Button>
                  </Box>
                  <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2 }}>
                    {chemistRows.length === 0 ? (
                      <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                        No pharmacy data for this range.
                      </Typography>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead>
                          <tr style={{ borderBottom: `2px solid ${HOLA_DARK}` }}>
                            <th style={{ textAlign: "left", padding: "8px 4px", color: HOLA_DARK }}>Pharmacy</th>
                            <th style={{ textAlign: "right", padding: "8px 4px", color: HOLA_DARK }}>Appointments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chemistRows.map((r) => (
                            <tr key={r.chemistId} style={{ borderBottom: "1px solid #e5e7eb" }}>
                              <td style={{ padding: "7px 4px" }}>{r.chemistName}</td>
                              <td style={{ padding: "7px 4px", textAlign: "right", fontWeight: 600 }}>
                                {r.appointmentCount.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Consult type pie */}
              <Box
                sx={{
                  width: { xs: "100%", lg: 420 },
                  flex: { xs: "none", lg: "0 0 auto" },
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { xs: 360, lg: 0 },
                  alignSelf: { lg: "stretch" },
                }}
              >
                {pieSlices.length ? (
                  <Box sx={{ flex: isLgUp ? 1 : "none", minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <ChartCard
                      title="Appointments by consult type"
                      hideMainChart
                      fillParentHeight={isLgUp}
                      pieChartMinHeight={isLgUp ? 320 : 400}
                      data={{}}
                      primaryXAxis={{}}
                      primaryYAxis={{}}
                      borderClass="border-4"
                      series={[]}
                      pieData={pieSlices}
                      pieTitle="By consult type"
                      pieStartAngle="0"
                      pieEndAngle="360"
                      pieInnerRadius="54%"
                      pieLegendSettings={{
                        enablePages: false,
                        textStyle: { fontWeight: "500", size: "11px" },
                      }}
                    />
                  </Box>
                ) : (
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: HOLA_DARK,
                      borderRadius: 2,
                      flex: isLgUp ? 1 : "none",
                      minHeight: isLgUp ? 0 : 400,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                    }}
                  >
                    <Typography color="text.secondary" textAlign="center">
                      No consult type totals for this range.
                    </Typography>
                  </Card>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </div>

      {/* Appointments over time dialog */}
      <Dialog
        open={timeChartOpen}
        onClose={() => setTimeChartOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ color: HOLA_DARK, fontWeight: 700 }}>
          Hola appointments over time
        </DialogTitle>
        <DialogContent>
          <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
            <HubMedAppointmentsChart
              appointmentsPerDay={overview?.appointmentsPerDay}
              loading={loading && !overview}
              visible
              mainChartMinHeight={360}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeChartOpen(false)} sx={{ color: HOLA_DARK }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HolaAdminOverviewPage;
